// frontend/src/services/lateArrivalDebtsService.js
import api from './api';

/**
 * Service for managing late arrival debts
 */
const lateArrivalDebtsService = {
  /**
   * Get all late arrival debts with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with debts data
   */
  async getLateArrivalDebts(params = {}) {
    try {
      const response = await api.get('/admin/late-arrival-debts', { params });
      return response.data;
    } catch (error) {
      console.error('Get late arrival debts error:', error);
      throw error;
    }
  },

  /**
   * Get late arrival debts summary statistics
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with summary data
   */
  async getSummary(params = {}) {
    try {
      const response = await api.get('/admin/late-arrival-debts/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Get late arrival summary error:', error);
      throw error;
    }
  },

  /**
   * Get late arrival debts for a specific employee
   * @param {number} employeeId - Employee ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with employee debts
   */
  async getEmployeeDebts(employeeId, params = {}) {
    try {
      const response = await api.get(`/admin/late-arrival-debts/employee/${employeeId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Get employee late arrival debts error:', error);
      throw error;
    }
  },

  /**
   * Excuse a late arrival
   * @param {number} id - Late arrival debt ID
   * @param {Object} data - Excuse data (excuseReason, notes)
   * @returns {Promise} Response
   */
  async excuseLateArrival(id, data) {
    try {
      const response = await api.patch(`/admin/late-arrival-debts/${id}/excuse`, data);
      return response.data;
    } catch (error) {
      console.error('Excuse late arrival error:', error);
      throw error;
    }
  },

  /**
   * Remove excuse from a late arrival
   * @param {number} id - Late arrival debt ID
   * @returns {Promise} Response
   */
  async removeLateArrivalExcuse(id) {
    try {
      const response = await api.patch(`/admin/late-arrival-debts/${id}/remove-excuse`);
      return response.data;
    } catch (error) {
      console.error('Remove late arrival excuse error:', error);
      throw error;
    }
  },

  /**
   * Delete a late arrival debt record
   * @param {number} id - Late arrival debt ID
   * @returns {Promise} Response
   */
  async deleteLateArrival(id) {
    try {
      const response = await api.delete(`/admin/late-arrival-debts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete late arrival error:', error);
      throw error;
    }
  },

  /**
   * Export late arrivals to CSV
   * @param {Object} params - Query parameters
   * @returns {Promise} CSV file download
   */
  async exportLateArrivals(params = {}) {
    try {
      const response = await api.get('/admin/late-arrival-debts/export', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `late-arrivals-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Export late arrivals error:', error);
      throw error;
    }
  }
};

export default lateArrivalDebtsService;
